<template>
	<div
		ref="ref__dropdown"
		class="container__dropdown"
	>
		<div class="container__dropdown__label">
			<label>{{ label }}</label>
		</div>
		<div
			class="container__dropdown__component"
			:class="help.type"
		>
			<label
				:ref="`ref__dropdown__default_${uniqueRef}`"
				class="container__dropdown__component__default"
				@click="switchDropdown"
			>
				<p class="label__current_label">{{ atualLabel }}</p>
				<div class="left__sided_info">
					<i :class="['fas fa-angle-down', { active: showDropdown }]" />
				</div>
			</label>

			<ul
				:ref="`ref__dropdown__list_${uniqueRef}`"
				v-closable="{
					exclude: [`ref__dropdown__list_${uniqueRef}`, `ref__dropdown__default_${uniqueRef}`],
					handler: 'closeDropdown',
				}"
				:class="[
					'container__dropdown__component__list',
					{ 'open--dropdown': showDropdown },
					dropdownDirection,
				]"
			>
				<li>
					<div class="container__dropdowncheckboxfk__dropdown__filter">
						<!-- Sem @input: dropdownFiltred é COMPUTED (o v-model do textFilter
						     já refiltra a lista); usá-lo como handler fazia o Vue invocar o
						     array retornado como função — "t.apply is not a function" a
						     cada tecla digitada na busca. -->
						<input
							v-model="textFilter"
							type="text"
							class="container__dropdowncheckboxfk__dropdown__filter__input"
							:placeholder="$t('search')"
						/>
						<i class="fas fa-search icon" />
					</div>
				</li>
				<li
					v-if="options.length"
					style="display: flex; justify-content: space-evenly"
				>
					<p
						style="margin-bottom: 0px; cursor: pointer"
						@click="selectAll"
					>
						{{ $t('selectAll') }}
					</p>
					<p
						style="margin-bottom: 0px; cursor: pointer"
						@click="value.splice(0, value.length)"
					>
						{{ $t('deleteSelected') }}
					</p>
				</li>

				<li :class="['container__dropdown__component__list__item']">
					{{ $t('selectAnOption') }}
				</li>

				<li
					v-for="(option, index) in dropdownFiltred"
					:id="option.value"
					:key="index"
					:class="[
						'container__dropdown__component__list__item',
						{ active: value.includes(option.value), blocked: option.blocked },
					]"
					@click="setValue($event)"
				>
					{{ option.label }}
				</li>
			</ul>
		</div>
		<span
			v-show="Object.entries(help).length > 0"
			class="message__help__dropdown"
			>{{ help.message }}</span
		>
	</div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'

export default {
	name: 'MultiSelect',

	props: {
		/**
		 * mensagem de ajuda que pode ser mostrada abaixo dropdown
		 */
		help: {
			type: Object,
			default: () => {
				return { message: '', type: '' }
			},
			required: false,
		},
		/**
		 * Label do que estará escrito acima do componente de Dropdown
		 */
		label: {
			type: String,
			default: '',
		},
		/**
		 * Opções contendo uma lista de value e label para cada opção do dropdown
		 */
		options: Array,
		/**
		 * Valor selecionado
		 */
		value: {
			type: Array,
		},
		/**
		 * Permite seleção múltipla de opções
		 */
		multiple: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['update:modelValue'],

	data() {
		return {
			/**
			 * Define a direcao que sera aberto o dropdown, para baixo ou para cima
			 */
			dropdownDirection: 'downward',
			/**
			 * Deve mostrar ou não o dropdown
			 */
			showDropdown: false,
			/**
			 * Valores selecionados pelo usuário
			 */
			values: [],

			/**
			 * Valor selecionado
			 */
			selectedOptions: [],

			textFilter: '',
		}
	},
	computed: {
		/**
		 * Retorna o label que deve ser exibido dentro do dropdown
		 */
		atualLabel() {
			return this.value.length > 1
				? this.$t('multiSelect')
				: this.value.length === 0
					? this.$t('selectAnOption')
					: this.options.find((f) => this.value[0] === f.value).label
		},
		/**
		 * Retorna um Guid para o componente ter como referencia
		 * um valor unico por componente
		 */
		uniqueRef() {
			return uuidv4()
		},

		dropdownFiltred() {
			if (this.options.length > 0) {
				const textFilter = this.textFilter.toLowerCase()
				const columns = Object.keys(this.options[0]).filter((column) => column !== 'id')

				/**
				 * Realizar o filtro de acordo com o que foi digitado
				 */
				return this.options.filter((item) => {
					const model = _.pick(item, columns)
					const atualModel = Object.values(model).join(' ')
					return atualModel.toLowerCase().includes(textFilter)
				})
			}

			return []
		},
	},

	methods: {
		selectAll() {
			var newOptions = this.dropdownFiltred.filter(
				(option) => !this.value.includes(option.value) && !option.blocked,
			)
			this.value.push(...newOptions.map((m) => m.value))
		},

		/**
		 * Fecha o dropdown
		 */
		closeDropdown() {
			this.showDropdown = false
		},

		/**
		 * Retorna o label que deve ser exibido no dropdown
		 */
		getLabel(option) {
			return option.label ?? option.value
		},

		/**
		 * Retorna se a opcao passada por parametro e a que esta ativa
		 */
		isActiveOption(value) {
			return this.value.value === value
		},

		/**
		 * Alterna entre mostrar ou nao o dropdown
		 */
		switchDropdown() {
			this.showDropdown = !this.showDropdown
			if (this.showDropdown) {
				this.setDropdownDirection()
			}
		},

		/**
		 * Seta a direcao que o dropdown vai ser aberto
		 */
		setDropdownDirection() {
			const halfScreen = window.innerHeight / 2
			const elementPosition = this.$refs.ref__dropdown.getBoundingClientRect().top

			if (elementPosition > halfScreen) {
				this.dropdownDirection = 'upward'
			} else {
				this.dropdownDirection = 'downward'
			}
		},

		/**
		 * Seta o valor selecionado
		 */
		setValue(value) {
			const option = this.options.find((o) => o.value.toString() === value.target.id)
			if (option.blocked === true) return

			if (this.value.includes(option.value)) {
				this.value.splice(this.value.indexOf(option.value), 1)
			} else {
				this.value.push(option.value)
			}
		},
	},
}
</script>

<style scoped>
.left__sided_info {
	display: flex;
}

.container__dropdown {
	display: flex;
	flex-direction: column;
	width: 100%;
	user-select: none;
}

.container__dropdown__label {
	color: var(--color-navy-blue);
	font-weight: 400;
	margin-top: 0.5rem !important;
	margin-bottom: 0px !important;
}

.container__dropdown__component {
	position: relative;
}

.container__dropdown__component__default {
	border-radius: 4.5px;
	width: 100%;
	display: flex;
	height: 2rem;
	align-items: center;
	color: var(--color-navy-blue);
	font-weight: 400;
	cursor: pointer;
	padding: 0 1rem;
	display: flex;
	font-size: 0.75rem;
	justify-content: space-between;
}

.container__dropdown__component__default i {
	transition: transform 0.1s ease-in-out;
}
.container__dropdown__component__default i.active {
	transform: rotate(-180deg);
}

.container__dropdowncheckboxfk__dropdown__filter {
	display: flex;
	align-items: center;
	border: 1px solid var(--color-gray);
	margin: 0.5rem;
	border-radius: 4.5px;
}

.container__dropdowncheckboxfk__dropdown__filter i {
	color: rgb(var(--color-default));
	margin-right: 0.5rem;
}

.container__dropdowncheckboxfk__dropdown__filter__input {
	border: none;
	width: 100%;
	height: 32px;
	font-size: 0.75rem;
	padding: 0.5rem;
	background-color: transparent;
	outline: none;
}

.container__dropdown__component__list {
	position: absolute;
	border-left: 1px solid var(--color-gray);
	border-right: 1px solid var(--color-gray);
	background-color: white;
	width: 100%;
	max-height: 0;
	overflow: hidden;
	overflow-y: auto;
	z-index: 1;
	transition: max-height 0.1s ease-in-out;
	list-style-type: none;
	padding-left: 0;
}
.container__dropdown__component__list__item.blocked {
	cursor: not-allowed;
	color: gray;
}

.container__dropdown__component__list.downward {
	border-bottom-left-radius: 5px;
	border-bottom-right-radius: 5px;
	top: 1.9rem;
}

.container__dropdown__component__list.upward {
	bottom: 1.8rem;
	border-top-left-radius: 5px;
	border-top-right-radius: 5px;
}

.container__dropdown__component__list.open--dropdown {
	max-height: 30vh;
}

.container__dropdown__component__list.open--dropdown.downward {
	border-bottom: 1px solid var(--color-gray);
}

.container__dropdown__component__list.open--dropdown.upward {
	border-top: 1px solid var(--color-gray);
}

.container__dropdown__component__list__item {
	padding: 0.3rem 1rem 0.3rem 1rem;
	cursor: pointer;
	font-size: 0.8rem;
	border-bottom: 1px solid rgba(var(--color-neutral), 0.5);
	transition: all 0.1s ease-in-out;
}

.container__dropdown__component__list__item.active {
	background-color: rgba(var(--color-primary), 0.05);
	font-weight: 700;
}

.container__dropdown__component__list__item:hover {
	background-color: rgba(var(--color-primary), 0.1);
}

.label__current_label {
	margin: 0;
}

.message__help__dropdown {
	margin: 0px;
	font-size: 0.7rem;
}
</style>
